import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  ModalClose,
  Typography,
  useTheme,
} from "@mui/joy";
import { useMediaQuery } from "@mui/material";
import { useTour } from "@reactour/tour";
import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { CallMasthead } from "../atoms/callMasthead";
import { Paper } from "../atoms/paper";
import { ConfettiCenterContext } from "../context/confettiCenter/confettiCenter";
import { callTheme } from "../entities/callDesign";
import { Inspector } from "../entities/missionMap/missionMap";
import { uploadInspectorMessages } from "../helpers";
import { Footer } from "../organisms/footer";
import { Messages } from "../organisms/messages";
import { ProjectsList } from "../organisms/projectsList";
import { SeasonGreetings } from "../organisms/seasonGreetings";
import { rhapsodyApi } from "../redux/api";
import { useAnswerMutation } from "../redux/missionMap";
import {
  answersSelector,
  selectedTokenSelector,
  setAnswers,
  setSelectedToken,
} from "../redux/rhapsodyReducer";
import { useUploadFileMutation } from "../redux/upload";
import { ModernSeasonInvite } from "../templates/modernSeasonInvite";
import { ProjectInvite, useMissionMap } from "../templates/projectInvite";

/**
 *
 * @returns {ReactElement} SeasonInvite page
 */
export function ProjectsInvite() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");
  const dispatch = useDispatch();
  const { setIsOpen, setCurrentStep, setSteps } = useTour();
  const selectedToken = useSelector(selectedTokenSelector);
  const mission = useMissionMap(token!);
  // Modern Calls power-up: server-gated via the mapping, see callDesign.ts.
  const call = callTheme(mission);
  const [loading, setLoading] = useState(false);
  const answers = useSelector(answersSelector);
  const confettiCenter = useContext(ConfettiCenterContext);
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down(600));
  const [answer] = useAnswerMutation();
  const [upload] = useUploadFileMutation();

  // Nothing here leaves the browser before "Submit Answers", so the browser
  // is where answers must survive until then.
  const storageKey = `seasonAnswers-${token}`;

  // Files and signatures cannot be stored; everything else a musician chose
  // or typed can. Their absence is why this strips rather than serializes.
  const persistableAnswers = () => {
    const out: typeof answers = {};
    for (const key in answers) {
      if (!Object.prototype.hasOwnProperty.call(answers, key)) continue;
      const entry = answers[key];
      const source = entry.inspectors ?? {};
      const inspectors: NonNullable<(typeof entry)["inspectors"]> = {};
      for (const id in source) {
        if (!Object.prototype.hasOwnProperty.call(source, id)) continue;
        const numericID = Number(id);
        const inspector = source[numericID];
        if (!inspector) continue;
        const keep = ["string", "number", "boolean"].includes(
          typeof inspector.message,
        );
        inspectors[numericID] = {
          ...inspector,
          message: keep ? inspector.message : undefined,
        };
      }
      out[key] = { ...entry, inspectors };
    }
    return out;
  };

  // Mail apps open play links in in-app browsers that get killed without any
  // beforeunload — a musician half-way through the season must find their
  // choices again when they reopen the link. Anything already in the store
  // (this session's work) wins over what was stored.
  useEffect(() => {
    if (!token) return;
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) ?? "null");
      if (stored) dispatch(setAnswers({ ...stored, ...answers }));
    } catch {
      // Corrupted storage is no reason to break the page.
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(persistableAnswers()));
    } catch {
      // Storage full or blocked — the page still works, answers just won't
      // survive the tab.
    }
  }, [answers, token]);

  // While any answer is recorded but not submitted, leaving the page loses
  // work — the browser's native "changes may not be saved" dialog is the only
  // warning it will show.
  const unsubmitted = Boolean(
    mission?.Projects?.some(
      (project) =>
        project.Interactors?.length > 0 &&
        answers[project.PublicToken]?.selectedInteractor,
    ),
  );
  useEffect(() => {
    if (!unsubmitted) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome only shows the dialog when returnValue is set.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);

    return () => window.removeEventListener("beforeunload", warn);
  }, [unsubmitted]);

  if (!mission) return <Box />;

  const missingAnswer = (key: string) => {
    if (setSteps)
      setSteps([
        {
          selector: `.${key}`,
          content: (
            <Box>
              <Typography level="title-md" sx={{ color: "inherit" }}>
                Missing Response
              </Typography>
              <Typography level="body-sm" sx={{ color: "inherit" }}>
                Open this project and provide an answer
              </Typography>
            </Box>
          ),
        },
      ]);
    setIsOpen(false);
    setCurrentStep(0);
    setIsOpen(true);
  };

  const handleSubmit = async () => {
    if (mission?.PublicToken[0] !== "G") return;

    setLoading(true);
    try {
      const body: {
        [key: string]: {
          mercuryInteractorID?: number;
          jobStageID?: number;
          mercuryInspectors?: Inspector[];
          grouped?: boolean;
        };
      } = {};
      for (const key in mission.Projects) {
        if (Object.prototype.hasOwnProperty.call(mission.Projects, key)) {
          const project = mission.Projects[key];
          const a = answers[project.PublicToken];

          if (project?.Interactors?.length === 0) {
            continue;
          }
          // allAnswered() vouches for this before anything is sent, but a
          // project must still never go up without its answer: an undefined
          // interactor is not an answer, whatever the server makes of it.
          if (!a?.selectedInteractor) {
            continue;
          }

          const ins = await uploadInspectorMessages(
            a?.inspectors ?? {},
            upload,
          );

          body[project.PublicToken] = {
            mercuryInteractorID: a?.selectedInteractor?.ID,
            mercuryInspectors: ins,
            jobStageID: a?.selectedInteractorJobStageID,
            grouped: token?.substring(1) === project?.PublicToken,
          };
        }
      }

      if (Object.keys(body).length === 0) return;

      await answer(body).unwrap();
      // Sent and settled: the browser copy has served its purpose.
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // Best effort — a stale draft is harmless once the projects resolve.
      }
      confettiCenter.show();
    } catch {
      // The API middleware surfaces the failure as a notification, and the
      // answers stay recorded for another try. The mapping is refetched
      // because the server commits every answer it can even when one fails:
      // resolved projects drop out, so a retry only re-sends what is still
      // open instead of double-answering what already landed.
      dispatch(rhapsodyApi.util.invalidateTags(["missions"]));
    } finally {
      setLoading(false);
    }
  };

  const allAnswered = () => {
    for (const key in mission.Projects) {
      if (Object.prototype.hasOwnProperty.call(mission.Projects, key)) {
        const a = mission.Projects[key];
        if (a.Interactors.length === 0) continue;
        if (!answers[a.PublicToken]) {
          missingAnswer(a.PublicToken);
          return false;
        }
      }
    }

    for (const key in answers) {
      if (Object.prototype.hasOwnProperty.call(answers, key)) {
        const a = answers[key];
        const b = mission.Projects.find((p) => p.PublicToken === key);
        // Answers keyed by a token that is not one of the season's
        // projects (e.g. the season token itself) must not block submission.
        if (!b || b.Interactors.length === 0) continue;
        if (!a.selectedInteractor) {
          missingAnswer(key);
          return false;
        }
      }
    }
    return true;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: call.ground,
        alignItems: "center",
        position: "relative",
        flex: 1,
      }}
    >
      <Helmet>
        <meta name="theme-color" content={call.themeColor} />
        <title>{mission.CompanyName}</title>
      </Helmet>
      {/* Once, above the column. This used to render here AND again inside the
          column below, so a season with any message showed two identical chip
          bars. Polls belong in the gate too — a season can have a poll and no
          messages, and the bar is how a poll is reached. */}
      {mission?.Messages.length || mission?.Polls?.length ? (
        <Messages token={token ?? ""} />
      ) : (
        []
      )}
      <Box
        sx={{
          // The modern season mirrors the call's 680px column; legacy keeps
          // its historical 480px one untouched.
          maxWidth: call.modern ? 680 : 480,
          width: "100%",
          p: call.modern ? 2 : 1,
          gap: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: call.modern ? "start" : "center",
        }}
      >
        {call.modern ? (
          <CallMasthead
            theme={call}
            companyName={mission.CompanyName}
            kind={mission.EventStatusTitle}
          />
        ) : (
          <img
            src={mission.CompanyLogo}
            style={{ height: 70, margin: "auto" }}
          />
        )}
        {call.modern ? (
          // Modern Calls power-up: the season is answered card by card, in
          // place — see templates/modernSeasonInvite.tsx.
          <ModernSeasonInvite
            mission={mission}
            theme={call}
            submitting={loading}
            onSubmit={handleSubmit}
          />
        ) : (
          <>
            <SeasonGreetings
              token={mission.PublicToken}
              seasonCover={mission.CoverURL}
              seasonName={mission.SeasonName}
            />
            {/* <Response token={token ?? ""} /> */}
            <ProjectsList mission={mission} />
            {mission.allInteractors()?.length > 0 ? (
              <Paper>
                <Typography
                  startDecorator={
                    <i className="fa-solid fa-arrow-down-long fa-bounce"></i>
                  }
                  endDecorator={
                    <i className="fa-solid fa-arrow-down-long fa-bounce"></i>
                  }
                >
                  Click here to submit all your answers
                </Typography>
                <Button
                  startDecorator={<i className="fa-solid fa-calendar-plus"></i>}
                  variant="solid"
                  color="primary"
                  fullWidth
                  onClick={() => {
                    if (allAnswered()) {
                      handleSubmit();
                    }
                  }}
                >
                  Submit Answers
                </Button>
              </Paper>
            ) : (
              []
            )}
            <Footer token={mission.PublicToken} />
          </>
        )}
      </Box>
      {loading ? (
        <Box
          style={{
            position: "fixed",
            background: "rgba(0,0,0,0.6)",
            height: "100vh",
            width: "100vw",
            top: 0,
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(10px)",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        []
      )}
      {/* The side drawer is the legacy way into a project. The modern deck
          shows each project full screen instead, so it never opens one. */}
      <Drawer
        sx={{
          zIndex: 100002,
          display: call.modern ? "none" : undefined,
        }}
        onClose={() => dispatch(setSelectedToken(undefined))}
        open={!call.modern && selectedToken !== undefined}
        anchor="right"
        slotProps={{
          content: {
            sx: {
              width: "100vw",
              maxWidth: 480,
            },
          },
        }}
      >
        {sm ? (
          <Box
            sx={{
              zIndex: 100002,
              background: call.ground,
            }}
          >
            <Button
              onClick={() => dispatch(setSelectedToken(undefined))}
              variant="plain"
              size="lg"
              startDecorator={<i className="fa-solid fa-chevron-left"></i>}
            >
              Back
            </Button>
          </Box>
        ) : (
          <ModalClose sx={{ zIndex: 9999 }} />
        )}
        {selectedToken ? (
          <ProjectInvite token={selectedToken} hideLogo hideFooter />
        ) : (
          []
        )}
      </Drawer>
    </Box>
  );
}
